/**
 * Mutation hook for POST /agent/tickets/{ticket_number}/merge.
 *
 * The path ticket is the MASTER; every ticket in
 * ``source_ticket_numbers`` folds into it and becomes a routed
 * tombstone (replies to any source land on the master from then on).
 *
 * On success we invalidate the whole ["agent", "tickets"] prefix: a
 * merge rewrites lists, counts, detail, comments, events, watchers,
 * and attachments for BOTH sides at once, so surgical per-key
 * invalidation would just be a longer way to spell "everything".
 *
 * Plan: plans/2026-07-15-ticket-merge-and-thread-routing.md.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import api from "@/lib/api";
import type { TicketMergeResponse } from "@/types/api";

export function useMergeTickets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      targetTicketNumber: number;
      sourceTicketNumbers: number[];
    }): Promise<TicketMergeResponse> => {
      const response = await api.post<TicketMergeResponse>(
        `/agent/tickets/${args.targetTicketNumber}/merge`,
        { source_ticket_numbers: args.sourceTicketNumbers },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["agent", "tickets"] });
    },
  });
}
